import { inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { ApiAuthService, AuthService } from '@shared';
import { firstValueFrom } from 'rxjs';

const ALLOWED_ROLES = new Set(['admin', 'manager', 'support', 'superAdmin', 'zoneAdmin']);

export async function canActivateAdmin(): Promise<boolean | UrlTree> {
  const router = inject(Router);
  const authService = inject(AuthService);
  const apiAuthService = inject(ApiAuthService);

  try {
    // Check if JWT token exists and is valid (not expired)
    if (!apiAuthService.isAuthenticated()) {
      // If token exists but is expired, try to refresh
      const refreshToken = apiAuthService.getRefreshToken();
      if (refreshToken) {
        try {
          console.log('Token expired, attempting refresh...');
          await firstValueFrom(apiAuthService.refreshToken());
          console.log('Token refreshed successfully');
          // Token refreshed, continue with auth check
        } catch (error) {
          console.log('Token refresh failed, redirecting to login');
          // Refresh failed, clear tokens and redirect to login
          apiAuthService.clearTokens();
          return router.createUrlTree(['/login']);
        }
      } else {
        console.log('No JWT token found, redirecting to login');
        return router.createUrlTree(['/login']);
      }
    }

    // Get profile from auth service (should be set after login)
    const profile = authService.profile();
    
    // If profile not loaded yet, try to load it from JWT user data
    if (!profile) {
      const jwtUser = apiAuthService.getUser();
      if (jwtUser) {
        const userProfile = apiAuthService.convertToUserProfile(jwtUser);
        authService.profile.set(userProfile);
        
        // Check role
        if (jwtUser.role && ALLOWED_ROLES.has(jwtUser.role)) {
          return true;
        }
      }
      
      // Still no profile - redirect to login
      console.warn('Profile not found, redirecting to login');
      apiAuthService.clearToken();
      return router.createUrlTree(['/login']);
    }

    // Check if user has allowed role
    if (profile.role && ALLOWED_ROLES.has(profile.role)) {
      return true;
    }

    // User doesn't have required role
    console.warn('User does not have required role', { 
      role: profile.role,
      userId: profile.id,
      email: profile.email
    });
    
    apiAuthService.clearToken();
    return router.createUrlTree(['/login'], {
      queryParams: { 
        unauthorized: '1', 
        reason: 'unauthorized' 
      },
    });
  } catch (error) {
    console.error('AuthGuard failed:', error);
    apiAuthService.clearToken();
    return router.createUrlTree(['/login']);
  }
}

export async function redirectIfAuthenticated(): Promise<boolean | UrlTree> {
  const router = inject(Router);
  const authService = inject(AuthService);
  const apiAuthService = inject(ApiAuthService);

  try {
    // Check if JWT token exists and is valid (not expired)
    if (!apiAuthService.isAuthenticated()) {
      // If token exists but is expired, try to refresh
      const refreshToken = apiAuthService.getRefreshToken();
      if (refreshToken) {
        try {
          await firstValueFrom(apiAuthService.refreshToken());
          // Token refreshed, continue with auth check
        } catch (error) {
          // Refresh failed, allow access to login page
          return true;
        }
      } else {
        // Not authenticated, allow access to login page
        return true;
      }
    }

    // Get profile from auth service
    const profile = authService.profile();
    
    // If profile not loaded, try to load from JWT user data
    if (!profile) {
      const jwtUser = apiAuthService.getUser();
      if (jwtUser) {
        const userProfile = apiAuthService.convertToUserProfile(jwtUser);
        authService.profile.set(userProfile);
        
        if (jwtUser.role && ALLOWED_ROLES.has(jwtUser.role)) {
          return router.createUrlTree(['/dashboard']);
        }
      }
      return true; // Profile not found, allow access to login
    }

    // If user has allowed role, redirect to dashboard
    if (profile.role && ALLOWED_ROLES.has(profile.role)) {
      return router.createUrlTree(['/dashboard']);
    }

    return true; // User doesn't have required role, allow access to login
  } catch (error) {
    console.error('RedirectIfAuthenticated failed:', error);
    return true; // On error, allow access to login
  }
}
